import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Divider, Grid, Table, TableBody, TableCell, TableHead, TableRow, ToggleButtonGroup} from '@mui/material';
import uuidv4 from 'uuid/v4';
import {user} from '../../../../store/reducers/user';
import moment from 'moment';
import clsx from 'clsx';
import _, { set } from 'lodash';
import {additionalData} from '../../../../store/reducers/additionalData';
import Formatter from '../../../../util/formatter';
import MessageBox from '../../../../util/messageBox';
import ToggleButton from '@mui/material/ToggleButton';
import ObservationTable from '../../../../components/observation/observationTable';
import Notes from '../../../../util/notes';
import API from '../../../../app/api';
import {mapObservationFromAPI, obsTypes} from '../observation/obsUtils';
import {setSnackbar} from '../../../../store/reducers/globals';
import useOpenState from '../../../../hooks/useOpenState';
import SepsisTable from '../../../../components/observation/sepsisTable';
import InformationBox from '../../../../components/other/informationBox';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import FormLabel from '@mui/material/FormLabel';
import AddIcon from '@mui/icons-material/Add';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import ComputerIcon from '@mui/icons-material/Computer';
import Tooltip from '@mui/material/Tooltip';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import {workflow} from '../../../../store/reducers/workflow';
import VisualInfusionPhlebitisModal from './components/visualInfusionPhlebitisModal';
import VisualInfusionPhlebitisSelect from './components/visualInfusionPhlebitisSelect';
import {isSpecialSmallDeviceDetect} from '../../../../util/isSpecialSmallDeviceDetect';
import {setLoading} from '../../../../store/reducers/patient';
import {getPatientFlag, getPatientFlagValue} from '../../mainScreen/util/patientFlagsUtil';
import {patientSafetyChecklistSectionValidation, SEPSIS_TOOL_TYPE} from '../formsUtil';
import {SepsisPathwayStatus} from '../../sepsis/sepsisUtil';
import {getConfig} from '../../../../util/configUtil';
import {PATIENT_AGE_TYPE} from '../../../../util/patientContextUtil';
import {WORKFLOW_OPTIONS} from '../../../../util/workflowUtil';

const useStyles = makeStyles((theme) => ({
    button: {
        background: '#fff',
        color: '#000',
        '&:hover': {
            background: '#fff',
        },
    },
    header: {
        padding: 5,
        fontWeight: 'bold',
        color: '#000',
        borderRadius: 5,
        '&.margin': {
            marginTop: 10,
        },
    },
    row: {
        minHeight: 42,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '&.strikethrough': {
            textDecoration: 'line-through',
        },
        '&.small': {
            flexWrap: 'wrap',
        },
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    mandatory: {
        fontWeight: 'bold',
        color: '#e06666',
    },
    text: {
        width: '100%',
        marginTop: 5,
        fontSize: 14,
        fontFamily: 'Inter',
        fontWeight: 'bold',
    },
    creatorText: {
        color: theme.palette.mode === 'light' ? '#494B4C' : '#ACAFB3',
        fontSize: 12,
        fontFamily: 'Inter',
        fontWeight: 500,
        minHeight: 18,
    },
    categoryTitle: {
        fontWeight: 'bold',
        marginBottom: 10,
    },
    comment: {
        fontSize: 'small',
        marginTop: 5,
        padding: 5,
        backgroundColor: theme.palette.background.field,
        borderRadius: 5,
    },
    score: {
        backgroundColor: theme.palette.background.field,
        borderRadius: 5,
        maxWidth: 120,
        marginLeft: 'auto',
        padding: 5,

    },
    rowCell: {
        fontSize: 'small',
    },
    toggle: {
        width: 60,
        fontSize: 18,
    },
    strokeInfo: {
        marginTop: 10,
        fontStyle: 'italic',
        fontSize: 'small',
    },
    italic: {
        fontStyle: 'italic',
    },
    accordionMandatoryText: {
        marginLeft: 'auto',
        fontSize: '14px !important',
    },
    noBottomBorder: {
        borderBottom: 'none',
    },
}));

const PSC_DUE_TIME = [
    {name: '+ 1h', id: 1},
    {name: '+ 2h', id: 2},
    {name: '+ 3h', id: 3},
    {name: '+ 4h', id: 4},
];

const PatientSafetyChecklist = (props) => {
    const classesLocal = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const controller = new AbortController();

    const isSmallDevice = isSpecialSmallDeviceDetect();

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const version = getConfig('patientSafetyChecklist.clientVersion', 'DCH');
    const allMandatory = getConfig('patientSafetyChecklist.allFieldsMandatory', false);
    const IVCannulaMandatory = getConfig('patientSafetyChecklist.IVCannulaMandatory', false);
    const enableBedRequestForm = getConfig('modules.forms.bedBooking.enableBedRequestForm', false);
    const nursingAdmissionAvailable = getConfig('nursingAdmissionAvailable', false);
    const dementiaFlagEnabled = getConfig('dementiaFlagEnabled', false);
    const disableReadyToAdmission = getConfig('patientSafetyChecklist.disableReadyToAdmission', false);
    const disableRosierAssessment = getConfig('patientSafetyChecklist.disableRosierAssessment', false);

    const [formIsModified, setFormIsModified] = useState(false);

    //Workflow
    const agyleWorkflow = useSelector(workflow);
    const checkFlow = useRef(false);
    const firstLoad = useRef(true);

    // Additional data
    const patientAddData = useSelector(additionalData);
    const [observations, setObservations] = useState([]);
    const [canRefresh, setCanRefresh] = useState(false);

    // Load user
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Opener
    const [isConfirmNewSectionOpen, openConfirmNewSection, closeConfirmNewSection] = useOpenState(false, false);

    // Form State
    const [safetyChecklist, setSafetyChecklist] = useState(props.patient?.studies?.safetyChecklist || initialData());
    const [firstHourStart, setFirstHourStart] = useState(null);

    // Dementia
    const [dementiaTouch, setDementiaTouch] = useState(false);

    // Comment
    const [isCommentOpen, openComment, closeComment] = useOpenState(false, true);
    const [comment, setComment] = useState('');
    const field = useRef(null);
    const sections = useRef(null);

    // Accordion
    const [expanded, setExpanded] = useState('');

    // Validations
    const [sectionValidation, setSectionValidation] = useState({
        section1: false,
        section2: false,
        section3: false,
        section4: false,
    });
    const [isVipOpen, openVip, closeVip] = useOpenState(false, false);

    useEffect(() => {
        if (!firstLoad.current) checkFlow.current = true;
        firstLoad.current = false;
    }, [props.patient?.studies?.safetyChecklist]);

    useEffect(() => {
        // If Safety Checklist was open by workflow then check if there was update on patient object (e.g. Triage Form Saved)
        if (agyleWorkflow?.type === WORKFLOW_OPTIONS.SAFETY_CHECKLIST && checkFlow.current) {
            setCanRefresh(true);
        }
    }, [agyleWorkflow, props.patient, checkFlow.current]);

    useEffect(() => {
        if (props.patient?.studies?.safetyChecklist) {
            const psc = props.patient?.studies?.safetyChecklist;
            setSafetyChecklist(psc);

            if (!_.isEmpty(psc.nextHours)) {
                const last = _.last(psc.nextHours);
                if (last?._id) setExpanded(last._id);
            } else {
                const validSection1 = psc.section1Lock || (patientSafetyChecklistSectionValidation(psc, 1, props.patient?.patient) && (version === 'UHD' ? !_.isNil(safetyChecklist?.sectionDue1?.value) : true));
                const validSection2 = psc.section2Lock || (validSection1 && patientSafetyChecklistSectionValidation(psc, 2) && (version === 'UHD' ? !_.isNil(safetyChecklist?.sectionDue2?.value) : true));
                const validSection3 = psc.section3Lock || (validSection2 && patientSafetyChecklistSectionValidation(psc, 3) && (version === 'UHD' ? !_.isNil(safetyChecklist?.sectionDue3?.value) : true));

                if (validSection3) setExpanded('fourth');
                else if (validSection2) setExpanded('third');
                else if (validSection1) setExpanded('second');
                else setExpanded('first');
            }
        } else {
            setExpanded('first');
            setSafetyChecklist(initialData());
        }
    }, []);

    // Init data
    useEffect(() => {
        let dateToCheck;

        // Get time of Triage/S&T form completed
        if (props.patient?.triage && props.patient?.studies?.seeAndTreat) {
            dateToCheck = moment(props.patient?.studies?.seeAndTreat?.when).isAfter(moment(props.patient.triage?.when))
                ? props.patient.triage?.when
                : props.patient?.studies?.seeAndTreat?.when;
        } else {
            if (props.patient?.studies?.seeAndTreat) dateToCheck = props.patient?.studies?.seeAndTreat?.when;
            if (props.patient?.triage) dateToCheck = props.patient.triage?.when;
        }

        setFirstHourStart(dateToCheck);
        refreshObservation();
    }, []);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons([
                <AgyleButton
                    key={'PSC_1'}
                    disabled={!sectionValidation.section4}
                    onClick={openConfirmNewSection}
                >
                    {t('Add new Hour')}
                </AgyleButton>,
            ]);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(saveEntry);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(!formIsModified || props.disableSave);
        }
    }, [sectionValidation.section4, safetyChecklist, formIsModified, dementiaTouch]);

    useEffect(() => {
        if (canRefresh) {
            dispatch(setLoading(true));
            autoPopulation();
        }
    }, [observations, canRefresh]);

    // Check Validation
    useEffect(() => {
        const validSection1 = safetyChecklist.section1Lock || patientSafetyChecklistSectionValidation(safetyChecklist, 1, props.patient?.patient);
        const validSection2 = safetyChecklist.section2Lock || (validSection1 && patientSafetyChecklistSectionValidation(safetyChecklist, 2));
        const validSection3 = safetyChecklist.section3Lock || (validSection2 && patientSafetyChecklistSectionValidation(safetyChecklist, 3));
        const validSection4 = safetyChecklist.section4Lock || (validSection3 && patientSafetyChecklistSectionValidation(safetyChecklist, 4));
        setSectionValidation({
            section1: validSection1,
            section2: validSection2,
            section3: validSection3,
            section4: validSection4,
        });
    }, [safetyChecklist]);

    // Get all observations
    const refreshObservation = () => {
        const newType = props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? obsTypes.news : obsTypes.pews;
        const currentPatientInWard = props?.patient;
        API.getFullObservations(currentPatientInWard, newType, true, controller.signal)
            .then((result) => {
                let obs = [];
                _.forEach(result, (apiObs) => obs.push(mapObservationFromAPI(apiObs)));
                setObservations(obs);
                setCanRefresh(true);
            })
            .catch((error) => {
                console.error('Observations.API.getObservations: ', error);
            });
    };

    function initialData() {
        return {
            // Overall info
            _id: uuidv4() + '',
            by: tester,
            login: userObject?.userName,
            date: moment().toISOString(),
            lastUpdateBy: null,
            lastUpdateDate: null,

            // First Hour
            triage: null,
            obsFirst: null,
            obsEscalation: null,

            ECGRecorded: null,
            ECGReviewed: null,
            undressedAndGown: null,
            IDBand: null,
            painScore: null,
            analgesia: null,
            infectionScreening: null,
            patientAlertsFlags: null,
            diabetic: null,
            sepsisSuspected: null,
            sepsisComplete: null,
            sepsisSeniorSignOff: null,
            IVAccess: null,
            vipScore: null,
            bloodTests: null,

            strokeCall: null,
            specTeamsNotified: null,
            dementia: null,
            dementiaScore: null,
            clinicalFrailty: null,

            mentalHealth: null,
            nursingComments: null,

            sectionDue1: null,
            // Second Hour
            obsSecond: null,
            painScoreAssessedSecond: null,
            analgesiaSecond: null,
            nok: null,
            nokContacted: null,
            refreshmentSecond: null,
            pressureAreas: null,
            medicinesSecond: null,
            diabeticPatientsSecond: null,
            criticalDrugsGiven: null,

            mentalHealthSecond: null,
            nursingCommentsSecond: null,

            IVAccessSecond: null,
            vipScoreSecond: null,
            bloodTestsSecond: null,

            sectionDue2: null,
            // Third Hour
            obsThird: null,
            painScoreAssessedThird: null,
            analgesiaThird: null,
            medicinesThird: null,
            refreshmentThird: null,
            patientUpdatePlan: null,
            diabeticPatientsThird: null,

            mentalHealthThird: null,
            nursingCommentsThird: null,

            IVAccessThird: null,
            vipScoreThird: null,
            bloodTestsThird: null,

            sectionDue3: null,
            // Fourth Hour
            obsFourth: null,
            painScoreAssessedFourth: null,
            analgesiaFourth: null,
            refreshmentFourth: null,
            medicinesFourth: null,
            diabeticPatientsFourth: null,

            patientUpdatePlanFourth: null,
            mentalHealthFourth: null,
            nursingCommentsFourth: null,

            IVAccessFourth: null,
            vipScoreFourth: null,
            bloodTestsFourth: null,

            sectionDue4: null,
            // Stroke Check List
            stroke: {
                LOCOrSyncope: null,
                seizure: null,
                asymmetricFacial: null,
                asymmetricArm: null,
                asymmetricLeg: null,
                speech: null,
                visual: null,
            },

            // Patient Admission
            wardTransfer: null,
            bed: null,
            propertyFormComplete: null,

            // Dynamic Sections
            nextHours: [],

            // Completed section flag
            section1Lock: false,
            section2Lock: false,
            section3Lock: false,
            section4Lock: false,
        };
    }

    // Auto population
    const autoPopulation = () => {
        const completedSection1 = sectionValidation.section1;
        const completedSection2 = sectionValidation.section2;
        const completedSection3 = sectionValidation.section3;
        const completedSection4 = sectionValidation.section4;

        let data = {};
        // First Hour
        if (!completedSection1) data = populateSection1(data);
        // Second Hour
        if (completedSection1 && !completedSection2) data = populateSection2(data);
        // Third Hour
        if (completedSection2 && !completedSection3) data = populateSection3(data);
        // Fourth Hour
        if (completedSection3 && !completedSection4) data = populateSection4(data);

        // Admission
        const admitted = props.patient?.clinicalConclusion?.status === 'Admitted';
        data = {
            ...data,
            wardTransfer: {
                value: admitted,
                by: admitted ? props.patient?.clinicalConclusion?.who?.__description : '',
                date: admitted ? moment(props.patient?.clinicalConclusion?.when, defaultDateTimeFormat).toISOString() : '',
            },
        };
        let bedConfirm;

        if (enableBedRequestForm) {
            bedConfirm = props.patient?.studies?.bedRequest?.ready;
            data = {
                ...data,
                bed: {
                    value: bedConfirm,
                    by: bedConfirm ? props.patient?.studies?.bedRequest.readyUser : '',
                    date: bedConfirm ? moment(props.patient?.studies?.bedRequest.readyWhen).toISOString() : '',
                    comment: bedConfirm ? props.patient?.studies?.bedRequest.ward?.wardName : '',
                },
            };
        } else {
            bedConfirm = props.patient?.csmContactedTime?.bed === 'ready';
            data = {
                ...data,
                bed: {
                    value: bedConfirm,
                    by: bedConfirm ? props.patient?.csmContactedTime?.by?.__description : '',
                    date: bedConfirm ? moment(props.patient?.csmContactedTime?.when, defaultDateTimeFormat).toISOString() : '',
                    comment: bedConfirm ? props.patient?.csmContactedTime?.ward?.wardName : '',
                },
            };
        }

        if (nursingAdmissionAvailable && props.patient?.studies?.nursingAdmission) {
            let formConfirm = props.patient?.studies?.nursingAdmission;
            data = {
                ...data,
                propertyFormComplete: {
                    value: true,
                    by: formConfirm.lastUpdateBy,
                    date: moment(formConfirm.lastUpdateDate).toISOString(),
                    comment: '',
                },
            };
        }

        setSafetyChecklist(prevState => ({...prevState, ...data}));
        setCanRefresh(false);
        dispatch(setLoading(false));
    };

    const populateSection1 = (data) => {
        const lastObsFirstSection = getLatestObservation(1, false);
        const sepsis = getLatSepsis();
        const lastPainScore = getLastPainScore(1);

        let triage = safetyChecklist?.triage || null;
        let obsEscalation = safetyChecklist?.obsEscalation || null;
        let analgesia = safetyChecklist?.analgesia || null;

        if (!safetyChecklist?.analgesia?.manualCheck) analgesia = getAnalgesia(triage);
        if (!safetyChecklist?.triage?.manualCheck) triage = getTriageValue();
        if (!safetyChecklist?.obsEscalation?.manualCheck) obsEscalation = getEscalation(lastObsFirstSection?.totalScore);

        let obsFirst = safetyChecklist?.obsFirst || null;
        if (!safetyChecklist?.obsFirst?.manualCheck) {
            obsFirst = {
                value: !!lastObsFirstSection,
                by: lastObsFirstSection ? lastObsFirstSection?.completedByFullName : '',
                date: lastObsFirstSection ? moment(lastObsFirstSection.examinationEnd).toISOString() : '',
                comment: lastObsFirstSection?.extraOption3Reason ? lastObsFirstSection?.extraOption3Reason : '',
                obsUsed: lastObsFirstSection?.__id || null,
                obsDateUsed: lastObsFirstSection?.examinationEnd || null,
            };
        }

        let sepsisSuspected = safetyChecklist?.sepsisSuspected || null;
        if (!safetyChecklist?.sepsisSuspected?.manualCheck) {
            sepsisSuspected = {
                value: sepsis.pathway ? true : (lastObsFirstSection?.extraOption3 || false),
                by: sepsis.pathway ? sepsis.pathway?.userCreate : lastObsFirstSection?.extraOption3 ? lastObsFirstSection?.completedByFullName : '',
                date: sepsis.pathway ? moment(sepsis.pathway?.dateCreate).toISOString()
                    : lastObsFirstSection?.extraOption3 ? moment(lastObsFirstSection?.examinationEnd).toISOString() : '',
                comment: '',
            };
        }

        data = {
            ...data,
            sepsisComplete: sepsis.pathway?.statusObj === SepsisPathwayStatus.ENDED,
            triage,
            obsFirst,
            obsEscalation,
            analgesia,
            sepsisSuspected,
        };

        if (!safetyChecklist?.painScore?.manualCheck) {
            if (lastPainScore) {
                data = {
                    ...data,
                    painScore: {
                        value: true,
                        by: lastPainScore?.completedByFullName,
                        date: moment(lastPainScore?.completedDate).toISOString(),
                        comment: lastPainScore?.painScore?.painScore ? `Pain Score: ${lastPainScore?.painScore?.painScore}` : '',
                        obsUsed: lastPainScore?.__id,
                        obsDateUsed: lastPainScore?.startedDate,
                    },
                };
            } else {
                data = {
                    ...data,
                    painScore: null,
                };
            }
        }

        if (props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS) {
            const recentStudies = getLastDeliriumAndFrailtyScore(false);
            const dementiaScore = getDementiaValue();

            if (!safetyChecklist?.dementiaScore?.manualCheck) {
                if (dementiaScore) {
                    data = {
                        ...data,
                        dementiaScore,
                    };
                }
            }

            if (!safetyChecklist?.dementia?.manualCheck) {
                if (recentStudies.delirium) {
                    data = {
                        ...data,
                        dementia: {
                            value: true,
                            by: recentStudies.delirium?.by,
                            date: recentStudies.delirium.date,
                            comment: `Score: ${recentStudies.delirium?.totalScore}`,
                            assessmentUsed: recentStudies.delirium?.id,
                        },
                    };
                } else {
                    data = {
                        ...data,
                        dementia: null,
                    };
                }
            }

            if (!safetyChecklist?.clinicalFrailty?.manualCheck) {
                if (recentStudies.clinicalFrailtyScore) {
                    data = {
                        ...data,
                        clinicalFrailty: {
                            value: true,
                            by: recentStudies.clinicalFrailtyScore?.by,
                            date: recentStudies.clinicalFrailtyScore?.date,
                            comment: `Score: ${recentStudies.clinicalFrailtyScore?.scale?.__code}. ${recentStudies.clinicalFrailtyScore?.scale?.__description}`,
                            assessmentUsed: recentStudies.clinicalFrailtyScore?.id,
                        },
                    };
                } else {
                    data = {
                        ...data,
                        clinicalFrailty: null,
                    };
                }
            }
        }

        const nursingAdmission = props?.patient?.studies?.nursingAdmission;
        if (nursingAdmission) {
            let undressedAndGown = safetyChecklist?.undressedAndGown || null;
            if (!safetyChecklist?.undressedAndGown?.manualCheck && !_.isNil(nursingAdmission?.undressedIntoGown?.checkedDate)) {
                undressedAndGown = {
                    value: nursingAdmission?.undressedIntoGown?.value === 'yes',
                    notRequired: nursingAdmission?.undressedIntoGown?.value === 'notRecorded',
                    by: nursingAdmission?.undressedIntoGown?.checkedBy,
                    date: moment(nursingAdmission?.undressedIntoGown?.checkedDate).toISOString(),
                    comment: nursingAdmission?.undressedIntoGown?.comment,
                };
            }

            let IDBand = safetyChecklist?.IDBand || null;
            if (!safetyChecklist?.IDBand?.manualCheck && !_.isNil(nursingAdmission?.namebands?.checkedDate)) {
                IDBand = {
                    value: nursingAdmission?.namebands?.value === 'yes',
                    notRequired: nursingAdmission?.namebands?.value === 'notRecorded',
                    by: nursingAdmission?.namebands?.checkedBy,
                    date: moment(nursingAdmission?.namebands?.checkedDate).toISOString(),
                    comment: nursingAdmission?.namebands?.comment,
                };
            }

            data = {
                ...data,
                undressedAndGown,
                IDBand,
            };
        }
        return data;
    };

    const populateSection2 = (data) => {
        const lastObsSecondSection = getLatestObservation(2, false);
        const lastPainScoreSection = getLastPainScore(2);
        const nbm = getPatientFlag(props.patient, 'nbm');
        let nokText = '';

        // Prepare Next of Kin string
        if (props.patient?.patient?.nextOfKins) {
            _.forEach(props.patient.patient?.nextOfKins, nok => {
                nokText += `${_.capitalize(nok.firstName)} ${_.capitalize(nok.familyName)} ${nok?.relationship?.__description ? _.capitalize(nok.relationship.__description) : '-'}`;
            });
        }

        let nok = safetyChecklist?.nok?.manualCheck ? safetyChecklist?.nok : {comment: nokText !== '' ? nokText : 'No data available about Next of Kin'};
        let obsSecond = safetyChecklist?.obsSecond || null;

        if (!safetyChecklist?.nok?.manualCheck) {
            nok = {
                value: nokText !== '',
                comment: nokText || '',
            };
        }

        if (!safetyChecklist?.obsSecond?.manualCheck) {
            obsSecond = {
                value: !!lastObsSecondSection,
                by: lastObsSecondSection ? lastObsSecondSection?.completedByFullName : '',
                date: lastObsSecondSection ? moment(lastObsSecondSection.examinationEnd).toISOString() : '',
                obsUsed: lastObsSecondSection?.__id || null,
                obsDateUsed: lastObsSecondSection?.examinationEnd || null,
            };
        }

        let refreshmentSecond = safetyChecklist?.refreshmentSecond || null;
        if (!safetyChecklist?.refreshmentSecond?.manualCheck) {
            refreshmentSecond = {
                notRequired: nbm?.checked,
                by: nbm?.checked ? nbm?.by : '',
                date: nbm?.checked ? moment(nbm?.date, defaultDateTimeFormat).toISOString() : '',
                comment: nbm?.checked ? 'Nil by Mouth flag enabled' : '',
            };
        }

        data = {
            ...data,
            obsSecond,
            nok,
            refreshmentSecond,
        };

        if (version === 'UHD') {
            let IVAccessSecond = safetyChecklist?.IVAccessSecond || null;
            if (!safetyChecklist?.IVAccessSecond?.manualCheck) {
                IVAccessSecond = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            let bloodTestsSecond = safetyChecklist?.bloodTestsSecond || null;
            if (!safetyChecklist?.bloodTestsSecond?.manualCheck) {
                bloodTestsSecond = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            data = {
                ...data,
                bloodTestsSecond,
                IVAccessSecond,
            };
        }

        if (!safetyChecklist?.painScoreAssessedSecond?.manualCheck) {
            if (lastPainScoreSection) {
                data = {
                    ...data,
                    painScoreAssessedSecond: {
                        value: true,
                        by: lastPainScoreSection?.completedByFullName,
                        date: moment(lastPainScoreSection?.completedDate).toISOString(),
                        comment: lastPainScoreSection?.painScore?.painScore ? `Pain Score: ${lastPainScoreSection?.painScore?.painScore}` : '',
                        obsUsed: lastPainScoreSection?.__id,
                        obsDateUsed: lastPainScoreSection?.startedDate,
                    },
                };
            } else {
                data = {
                    ...data,
                    painScoreAssessedSecond: null,
                };
            }
        }
        return data;
    };

    const populateSection3 = (data) => {
        const lastObsSecondSection = getLatestObservation(3, false);
        const lastPainScoreSection = getLastPainScore(3);
        const nbm = getPatientFlag(props.patient, 'nbm');

        let obsThird = safetyChecklist?.obsThird || null;
        if (!safetyChecklist?.obsThird?.manualCheck) {
            obsThird = {
                value: !!lastObsSecondSection,
                by: lastObsSecondSection ? lastObsSecondSection?.completedByFullName : '',
                date: lastObsSecondSection ? moment(lastObsSecondSection.examinationEnd).toISOString() : '',
                obsUsed: lastObsSecondSection?.__id || null,
                obsDateUsed: lastObsSecondSection?.examinationEnd || null,
            };
        }

        let refreshmentThird = safetyChecklist?.refreshmentThird || null;
        if (!safetyChecklist?.refreshmentThird?.manualCheck) {
            refreshmentThird = {
                notRequired: nbm?.checked,
                by: nbm?.checked ? nbm?.by : '',
                date: nbm?.checked ? moment(nbm?.date, defaultDateTimeFormat).toISOString() : '',
                comment: nbm?.checked ? 'Nil by Mouth flag enabled' : '',
            };
        }

        data = {
            ...data,
            obsThird,
            refreshmentThird,
        };

        if (version === 'UHD') {
            let IVAccessThird = safetyChecklist?.IVAccessThird || null;
            if (!safetyChecklist?.IVAccessThird?.manualCheck) {
                IVAccessThird = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            let bloodTestsThird = safetyChecklist?.bloodTestsThird || null;
            if (!safetyChecklist?.bloodTestsThird?.manualCheck) {
                bloodTestsThird = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            data = {
                ...data,
                IVAccessThird,
                bloodTestsThird,
            };
        }

        if (!safetyChecklist?.painScoreAssessedThird?.manualCheck) {
            if (lastPainScoreSection) {
                data = {
                    ...data,
                    painScoreAssessedThird: {
                        value: true,
                        by: lastPainScoreSection?.completedByFullName,
                        date: moment(lastPainScoreSection?.completedDate).toISOString(),
                        comment: lastPainScoreSection?.painScore?.painScore ? `Pain Score: ${lastPainScoreSection?.painScore?.painScore}` : '',
                        obsUsed: lastPainScoreSection?.__id,
                        obsDateUsed: lastPainScoreSection?.startedDate,
                    },
                };
            } else {
                data = {
                    ...data,
                    painScoreAssessedThird: null,
                };
            }
        }
        return data;
    };

    const populateSection4 = (data) => {
        const lastObsSecondSection = getLatestObservation(4, false);
        const lastPainScoreSection = getLastPainScore(4);
        const nbm = getPatientFlag(props.patient, 'nbm');

        let obsFourth = safetyChecklist?.obsFourth || null;
        if (!safetyChecklist?.obsFourth?.manualCheck) {
            obsFourth = {
                value: !!lastObsSecondSection,
                by: lastObsSecondSection ? lastObsSecondSection?.completedByFullName : '',
                date: lastObsSecondSection ? moment(lastObsSecondSection.examinationEnd).toISOString() : '',
                obsUsed: lastObsSecondSection?.__id || null,
                obsDateUsed: lastObsSecondSection?.examinationEnd || null,
            };
        }

        let refreshmentFourth = safetyChecklist?.refreshmentFourth || null;
        if (!safetyChecklist?.refreshmentFourth?.manualCheck) {
            refreshmentFourth = {
                notRequired: nbm?.checked,
                by: nbm?.checked ? nbm?.by : '',
                date: nbm?.checked ? moment(nbm?.date, defaultDateTimeFormat).toISOString() : '',
                comment: nbm?.checked ? 'Nil by Mouth flag enabled' : '',
            };
        }

        data = {
            ...data,
            obsFourth,
            refreshmentFourth,
        };

        if (version === 'UHD') {
            let IVAccessFourth = safetyChecklist?.IVAccessFourth || null;
            if (!safetyChecklist?.IVAccessFourth?.manualCheck) {
                IVAccessFourth = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            let bloodTestsFourth = safetyChecklist?.bloodTestsFourth || null;
            if (!safetyChecklist?.bloodTestsFourth?.manualCheck) {
                bloodTestsFourth = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
            }

            data = {
                ...data,
                IVAccessFourth,
                bloodTestsFourth,
            };
        }

        if (!safetyChecklist?.painScoreAssessedFourth?.manualCheck) {
            if (lastPainScoreSection) {
                data = {
                    ...data,
                    painScoreAssessedFourth: {
                        value: true,
                        by: lastPainScoreSection?.completedByFullName,
                        date: moment(lastPainScoreSection?.completedDate).toISOString(),
                        comment: lastPainScoreSection?.painScore?.painScore ? `Pain Score: ${lastPainScoreSection?.painScore?.painScore}` : '',
                        obsUsed: lastPainScoreSection?.__id,
                        obsDateUsed: lastPainScoreSection?.startedDate,
                    },
                };
            } else {
                data = {
                    ...data,
                    painScoreAssessedFourth: null,
                };
            }
        }
        return data;
    };

    // Get last Sepsis Tool and Sepsis Pathway
    const getLatSepsis = useCallback(() => {
        let pathway = null;
        let tool = null;

        if (!_.isEmpty(patientAddData?.other?.sepsisPathways)) {
            const filteredSepsis = _.filter(patientAddData?.other?.sepsisPathways, sepsis => {
                return sepsis.statusObj !== SepsisPathwayStatus.CANCELED
                    && moment(sepsis.dateCreate).isAfter(moment(props.patient?.admission?.startDate))
                    && (props.patient?.admission?.endDate ? moment(sepsis.dateCreate).isSameOrBefore(moment(props.patient?.admission?.endDate)) : true);
            });
            const sortedSepsis = _.sortBy(filteredSepsis, (obj) => moment(obj.dateCreate)).reverse();
            pathway = _.first(sortedSepsis);
        }

        if (!_.isEmpty(patientAddData?.other?.sepsisTool)) {
            const filteredSepsis = _.filter(patientAddData?.other?.sepsisTool, sepsis => {
                return moment(sepsis.date).isAfter(moment(props.patient?.admission?.startDate))
                    && (sepsis.status === SEPSIS_TOOL_TYPE.COMPLETED || sepsis.status === SEPSIS_TOOL_TYPE.IN_COMPLETED)
                    && (props.patient?.admission?.endDate ? moment(sepsis.date).isSameOrBefore(moment(props.patient?.admission?.endDate)) : true);
            });
            const sortedSepsis = _.sortBy(filteredSepsis, (obj) => moment(obj.date)).reverse();
            tool = _.first(sortedSepsis);
        }
        return {
            tool,
            pathway,
        };
    }, [patientAddData]);

    const getLatestObservation = useCallback((hour, allObs) => {
        // Filter observation by given date range depending on hour section
        const filteredObs = _.filter(observations, obs => {
            if (firstHourStart) {
                let startSearchDate = hour === 1
                    ? moment(props.patient.admission.startDate)
                    : moment(firstHourStart).add(moment.duration(hour - 1, 'hours'));
                const endSearchDate = moment(firstHourStart).add(moment.duration(hour, 'hours'));
                if (obs.startedDate.isSameOrAfter(startSearchDate) && obs.startedDate.isSameOrBefore(endSearchDate))
                    return obs;
            } else {
                return obs;
            }
        });
        return allObs ? filteredObs : _.last(filteredObs);
    }, [observations, firstHourStart]);

    const getLastPainScore = (hour) => {
        const observations = getLatestObservation(hour, true);
        const filteredPainScore = _.filter(observations, obs => obs.painScore.painScore != null && obs.painScore.status === 'completed');
        return _.last(filteredPainScore);
    };

    const getTriageValue = () => {
        if (props.patient.triage) {
            const isTimeStamp = props.patient.triage?.triageClinicians && !props.patient.triage?.discriminator;
            const comment = isTimeStamp ? 'Timestamp Set'
                : `${props.patient.triage?.discriminator?.__description || ''} \\ ` +
                `${props.patient.triage?.presentation?.__description || ''} \\ ` +
                `${props.patient?.triage?.chiefComplaint?.__description ? props.patient.triage.chiefComplaint.__description : 'Chief Complaint not set'}`;

            return {
                value: true,
                by: props.patient.triage?.triageClinicians?.__description
                    ? props.patient.triage?.triageClinicians?.__description
                    : props.patient.triage?.who,
                date: moment(props.patient.triage?.when).toISOString(),
                comment,
            };
        } else {
            return {
                value: false,
                by: '',
                date: '',
                comment: 'Not Triaged',
            };
        }
    };

    const getEscalation = (score) => {
        const sepsis = getLatSepsis();
        let obsEscalation = {
            value: false,
            notRequired: true,
            by: '',
            date: '',
        };

        if (score >= 5) {
            if (sepsis?.tool) {
                obsEscalation = {
                    notRequired: false,
                    value: sepsis?.tool?.doctorReview?.check || false,
                    by: sepsis?.tool?.doctorReview?.date || '',
                    date: sepsis?.tool?.doctorReview?.by || '',
                };
            } else if (sepsis?.pathway) {
                obsEscalation = {
                    notRequired: false,
                    value: sepsis?.pathway?.informST3 || false,
                    by: sepsis?.pathway?.informST3CheckedByDate ? moment(sepsis?.pathway?.informST3CheckedByDate).toISOString() : '',
                    date: sepsis?.pathway?.informST3CheckedBy || '',
                };
            }
        }

        return obsEscalation;
    };

    const getAnalgesia = (triageObj) => {
        let analgesia = null;

        if (triageObj) {
            if (props.patient?.triage?.analgesia) {
                analgesia = {
                    value: true,
                    by: triageObj.by,
                    date: triageObj.date,
                    comment: props.patient?.triage?.analgesiaDetails,
                };
            } else if (props.patient?.triage?.analgesiaRefused) {
                analgesia = {
                    value: false,
                    notRequired: true,
                    by: triageObj.by,
                    date: triageObj.date,
                    comment: 'Analgesia Refused',
                };
            } else {
                analgesia = {
                    value: false,
                    notRequired: true,
                    by: triageObj.by ? triageObj.by : '',
                    date: triageObj.date ? triageObj.date : '',
                    comment: 'No Analgesia Given',
                };
            }

        }

        return analgesia;
    };

    const getLastDeliriumAndFrailtyScore = (all = false) => {
        const startSearchDate = moment(props.patient.admission.startDate);
        const endSearchDate = firstHourStart ? moment(firstHourStart).add(moment.duration(1, 'hours')) : moment();
        const filteredDelirium = _.filter(props.patient?.studies?.delirium?.list, element => {
            if (!element.canceled && moment(element.date).isSameOrAfter(startSearchDate) && moment(element.date).isSameOrBefore(endSearchDate))
                return element;
        });

        const filteredClinicalFrailty = _.filter(props.patient?.studies?.rockwood?.list, element => {
            if (!element.canceled && moment(element.date).isSameOrAfter(startSearchDate) && moment(element.date).isSameOrBefore(endSearchDate))
                return element;
        });
        return {
            delirium: all ? filteredDelirium : _.first(filteredDelirium),
            clinicalFrailtyScore: all ? filteredClinicalFrailty : _.first(filteredClinicalFrailty),
        };
    };

    const getDementiaFlag = () => {
        if (props.patient?.metadata?.patientFlags) {
            const dementia = _.find(props.patient?.metadata?.patientFlags, {name: 'dementia'});
            return dementia ? dementia : false;
        }
    };

    const getDementiaValue = () => {
        const dementia = getDementiaFlag();
        let dementiaScore;

        if (dementia) {
            dementiaScore = {
                value: dementia.checked,
                notRequired: false,
                by: dementia.by,
                date: dementia.date,
                timestamp: dementia.date,
            };
        }

        return dementiaScore;
    };

    const addOrReplaceSection = (arr, object) => {
        let index = _.findIndex(arr, {'_id': object._id});
        if (-1 === index) {
            arr.push(object);
        } else {
            arr[index] = object;
        }
    };

    const handleChangeList = (value, name, notRequired, sectionId, newSection, arraySection, isAutoPopulate, defaultComment) => {
        setFormIsModified(true);
        const timestamp = moment().toISOString();

        if (name === 'dementiaScore' && dementiaFlagEnabled) {
            setDementiaTouch(true);
        }

        // For dynamic section
        if (arraySection) {
            const clone = _.cloneDeep(safetyChecklist.nextHours);
            let section = _.cloneDeep(arraySection);

            if (notRequired) {
                section = {
                    ...section,
                    [newSection.field]: {
                        ...arraySection[newSection.field],
                        notRequired: notRequired,
                        value: false,
                        by: tester,
                        login: userObject?.userName,
                        date: timestamp,
                        timestamp,
                        manualCheck: Boolean(isAutoPopulate),
                    },
                };
            } else {
                section = {
                    ...section,
                    [newSection.field]: {
                        ...arraySection[newSection.field],
                        value: value,
                        notRequired: false,
                        by: tester,
                        login: userObject?.userName,
                        date: timestamp,
                        timestamp,
                        manualCheck: Boolean(isAutoPopulate),
                        comment: defaultComment || arraySection[newSection.field]?.comment,
                    },
                };
            }
            addOrReplaceSection(clone, section);
            setSafetyChecklist(prevState => ({
                ...prevState,
                lastUpdateBy: tester,
                lastUpdateLogin: userObject?.userName,
                lastUpdateDate: timestamp,
                nextHours: clone,
            }));
        } else {
            let clone = _.cloneDeep(safetyChecklist);

            // Lock section
            if (sectionId !== '' && sectionId !== 1) {
                let sectionLock = clone[`section${sectionId - 1}Lock`];
                sectionLock = sectionLock || sectionValidation[`section${sectionId - 1}`];
                clone = {
                    ...clone,
                    [`section${sectionId - 1}Lock`]: sectionLock,
                };
            }
            if (notRequired) {
                clone = {
                    ...clone,
                    lastUpdateBy: tester,
                    lastUpdateLogin: userObject?.userName,
                    lastUpdateDate: timestamp,
                    [name]: {
                        ...clone[name],
                        notRequired: notRequired,
                        value: false,
                        by: tester,
                        login: userObject?.userName,
                        date: timestamp,
                        timestamp,
                        manualCheck: Boolean(isAutoPopulate),
                    },
                };
            } else {
                clone = {
                    ...clone,
                    lastUpdateBy: tester,
                    lastUpdateLogin: userObject?.userName,
                    lastUpdateDate: timestamp,
                    [name]: {
                        ...clone[name],
                        value: value,
                        notRequired: false,
                        by: tester,
                        login: userObject?.userName,
                        date: timestamp,
                        timestamp,
                        manualCheck: Boolean(isAutoPopulate),
                        comment: defaultComment ? defaultComment : clone[name]?.comment,
                    },
                };
            }
            setSafetyChecklist(clone);
            // }
        }
    };

    const handleAddComment = () => {
        setFormIsModified(true);
        const timestamp = moment().toISOString();

        // For static section
        if (sections.current) {
            const clone = _.cloneDeep(safetyChecklist.nextHours);
            let section = _.cloneDeep(sections.current);

            section = {
                ...section,
                [field.current]: {
                    ...sections.current[field.current],
                    comment: comment,
                    by: tester,
                    login: userObject?.userName,
                    date: timestamp,
                },
            };

            addOrReplaceSection(clone, section);
            setSafetyChecklist(prevState => ({
                ...prevState,
                nextHours: clone,
            }));
        } else {
            let psc = _.cloneDeep(safetyChecklist);

            psc = {
                ...psc,
                [field.current]: {
                    ...psc[field.current],
                    comment: comment,
                    by: tester,
                    login: userObject?.userName,
                    date: timestamp,
                },
            };
            setSafetyChecklist(psc);
        }

        setComment('');
        field.current = null;
        sections.current = null;
        closeComment();
    };

    const handleChangeAccordion = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const strokeScore = () => {
        const stroke = safetyChecklist.stroke;
        return Number(stroke.LOCOrSyncope) + Number(stroke.seizure) + Number(stroke.asymmetricFacial) +
            Number(stroke.asymmetricArm) + Number(stroke.asymmetricLeg) + Number(stroke.speech) + Number(stroke.visual);
    };

    const prepareNote = () => {
        let safetyChecklistOld = props.patient?.studies?.safetyChecklist || initialData();
        return Notes.getPatientSafetyChecklistNote(safetyChecklist, safetyChecklistOld, props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS, sectionValidation.section1, sectionValidation.section2, sectionValidation.section3, strokeScore());
    };

    const handleCloseComment = (e, reason) => {
        if (reason !== 'backdropClick') {
            setComment('');
            field.current = null;
            sections.current = null;
            closeComment();
        }
    };

    const renderComment = () => {
        const handleCommentChange = (e) => {
            const {value} = e.target;
            setComment(value);
        };

        return (
            <>
                <div>
                    <FormLabel>
                        {t('Text')}
                    </FormLabel>
                </div>

                <AgyleTextField
                    debounce
                    name="comment"
                    value={comment}
                    onChange={handleCommentChange}
                    fullWidth
                    noBorder={false}
                    withNewBackground
                />
            </>
        );
    };

    const saveEntry = () => {
        const data = [{
            name: 'studies.safetyChecklist',
            value: safetyChecklist,
            noteToAdd: prepareNote(),
        }];

        if (dementiaTouch) {
            let patientFlags = _.cloneDeep(props.patient?.metadata?.patientFlags);
            const dementiaValue = safetyChecklist?.dementiaScore?.value;
            let flagsToReturn = [];
            let flagIndex;
    
            if (patientFlags) {
                flagsToReturn = patientFlags;
                flagIndex = _.findIndex(patientFlags, {name: 'dementia'});
            }
    
            const dementiaFlag = {
                name: 'dementia',
                checked: !!dementiaValue,
                by: tester,
                login: userObject?.userName,
                date: moment().toISOString(),
            };
    
            if (flagIndex) {
                flagsToReturn[flagIndex] = dementiaFlag;
            } else flagsToReturn.push(dementiaFlag);
    
            const notes = Notes.getPatientFlagsNote([dementiaFlag]);
    
            data.push({
                name: 'metadata.patientFlags',
                value: flagsToReturn,
                noteToAdd: notes,
            })
        }

        props.onChange(data);
    };

    // Add new section
    const addNewSection = () => {
        const clone = _.cloneDeep(safetyChecklist.nextHours);
        const last = _.last(clone);
        const count = clone.length;
        const check = !_.isEmpty(clone) ? patientSafetyChecklistSectionValidation(last, count === 2 ? 6 : 0) : true;

        if (check) {
            const counter = last ? last?.counter + 1 : 5;
            const nbm = getPatientFlag(props.patient, 'nbm');
            const lastObsSection = getLatestObservation(counter, false);
            const lastPainScore = getLastPainScore(counter);

            let obs = null;
            let painScore = null;

            if (lastObsSection) {
                obs = {
                    value: true,
                    by: lastObsSection?.completedByFullName,
                    date: moment(lastObsSection.examinationEnd).toISOString(),
                    comment: lastObsSection?.extraOption3Reason,
                    obsUsed: lastObsSection?.__id,
                    obsDateUsed: lastObsSection?.examinationEnd,
                };
            }

            if (lastPainScore) {
                painScore = {
                    value: true,
                    by: lastPainScore?.completedByFullName,
                    date: moment(lastPainScore?.completedDate).toISOString(),
                    comment: lastPainScore?.painScore?.painScore ? `Pain Score: ${lastPainScore?.painScore?.painScore}` : '',
                    obsUsed: lastPainScore?.__id,
                    obsDateUsed: lastPainScore?.startedDate,
                };
            }

            const refreshment = {
                notRequired: nbm?.checked,
                by: nbm?.checked ? nbm?.by : '',
                date: nbm?.checked ? moment(nbm?.date, defaultDateTimeFormat).toISOString() : '',
                comment: nbm?.checked ? 'Nil by Mouth flag enabled' : '',
            };
            let obj = {
                _id: uuidv4() + '',
                counter,
                createBy: tester,
                createByLogin: userObject?.userName,
                createDate: moment().toISOString(),
            };

            if (version === 'UHD') {
                const IVAccess = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };
                const bloodTests = {
                    value: false,
                    notRequired: false,
                    date: moment().toISOString(),
                };

                if (counter === 6) {
                    obj = {
                        ...obj,
                        reasonForDelay: null,
                        delayExplained: null,
                        transferredIntoHospitalBed: null,
                        nurseAware: null,
                        medicines: null,
                        mentalHealth: null,
                        nursingComments: null,
                        IVAccess,
                        vipScore: null,
                        bloodTests,
                        sectionDue: null,
                    };
                } else {
                    obj = {
                        ...obj,
                        obs,
                        painScore,
                        analgesia: null,
                        refreshment,
                        medicines: null,
                        patientUpdatePlan: null,
                        mentalHealth: null,
                        nursingComments: null,
                        IVAccess,
                        vipScore: null,
                        bloodTests,
                        sectionDue: null,
                    };
                }
            } else {
                obj = {
                    ...obj,
                    obs,
                    painScore,
                    analgesia: null,
                    refreshment,
                    medicines: null,
                    diabeticPatients: null,
                };
            }

            clone.push(obj);
            setSafetyChecklist(prevState => ({...prevState, nextHours: clone}));
        }

        if (!check) {
            dispatch(setSnackbar({
                type: 'warning',
                message: 'Please complete previous section before adding a new section.',
            }));
        }
        closeConfirmNewSection();
    };

    // Create row for a checklist
    const createRow = (array, sectionId, title, renderSepsisTable) => {
        const handleOpenComment = (element) => {
            field.current = element.field;
            setComment(safetyChecklist[element.field]?.comment || '');
            openComment();
        };

        const getValue = (el, type) => {
            const val = safetyChecklist[el];
            if (val?.notRequired) return null;
            else if (type === 'due') return val?.value || '';
            else if (type === 'vipScore') return val?.value;
            else if (_.isBoolean(val?.value)) return val.value;

            return -1;
        };

        const handleChangeSwitch = (value, element) => {
            const isNR = _.isNull(value);
            handleChangeList(value, element.field, isNR, sectionId, null, null, element.autoPopulateCheck, element.defaultComment);
        };

        const handleChangeDue = (value, element) => {
            handleChangeList(value, element.field, false, sectionId, null, null, element.autoPopulateCheck, element.defaultComment);
        };

        const handleChangeComment = (e, element) => {
            const {value} = e.target;
            let psc = _.cloneDeep(safetyChecklist);

            psc = {
                ...psc,
                [element.field]: {
                    ...psc[field.current],
                    comment: value,
                    by: tester,
                    login: userObject?.userName,
                    date: moment().toISOString(),
                },
            };
            setSafetyChecklist(psc);
        };

        const checkValue = (val) => {
            const {value, notRequired} = val || {};
            return ((!value) && (!notRequired)) || !val;
        };

        return (
            <>
                {title && <div className={classesLocal.categoryTitle}>{title}</div>}

                {renderSepsisTable && <SepsisTable patient={props.patient} onlyLast showTableOnEmpty={false}/>}

                {array.map((element, index) => {
                    let assessment = true;
                    const isVIPScore = element.type === 'vipScore';
                    let isVIPScored = false;
                    const isDue = element.type === 'due';

                    if (element.checkAssessment) {
                        if (safetyChecklist[element.field]?.obsUsed) {
                            const assessmentId = safetyChecklist[element.field]?.obsUsed;
                            assessment = _.find(observations, {__id: assessmentId});
                        } else if (safetyChecklist[element.field]?.assessmentUsed) {
                            const assessmentId = safetyChecklist[element.field]?.assessmentUsed;
                            const assessmentsForm = getLastDeliriumAndFrailtyScore(true);
                            assessment = !!_.find(assessmentsForm.delirium, {id: assessmentId})
                                || !!_.find(assessmentsForm.clinicalFrailtyScore, {id: assessmentId});
                        }
                    }

                    let isMandatory = (element.mandatory || element.mandatoryText) && checkValue(safetyChecklist[element.field]);

                    if (element.textFieldComment) {
                        isMandatory = isMandatory && _.isEmpty(safetyChecklist[element.field]?.comment);
                    }

                    if (element.mandatoryComment && safetyChecklist[element.field]?.value) {
                        isMandatory = _.isEmpty(safetyChecklist[element.field]?.comment);
                    }

                    if (isVIPScore) {
                        const val = Number(getValue(element.field, element.type));
                        if (_.isNumber(val) && !_.isNaN(val)) {
                            isMandatory = val > 1 ? _.isEmpty(safetyChecklist[element.field]?.comment) : false;
                            isVIPScored = true;
                        } else {
                            isMandatory = true;
                        }
                    }

                    const commentButtonMandatory = element.mandatoryComment && _.isEmpty(safetyChecklist[element.field]?.comment) && safetyChecklist[element.field]?.value === true;

                    return (
                        <div key={element.field}>
                            {index > 0 && <Divider style={{margin: '6px 0'}}/>}
                            <Grid
                                container
                                className={clsx(classesLocal.row, !assessment && 'strikethrough', isSmallDevice && 'small')}
                                columns={8}
                            >
                                <Grid item xs={8} sm={3} md={isVIPScore ? 4 : 5}>
                                    <div
                                        className={clsx(classesLocal.text, isMandatory && classesLocal.mandatory)}
                                    >
                                        {t(element.name)}{isMandatory ? '*' : ''}
                                    </div>

                                    <div className={classesLocal.creatorText}>
                                        {`${safetyChecklist[element.field]?.by || ''} ${safetyChecklist[element.field]?.date
                                            ? moment(safetyChecklist[element.field].date).format(defaultDateTimeFormat)
                                            : ''
                                        }`}
                                    </div>
                                </Grid>

                                <Grid
                                    item
                                    xs={8}
                                    sm={isVIPScore ? 8 : 3}
                                    md={isVIPScore ? 3 : 2}
                                    className={classesLocal.divRow}
                                    justifyContent={isSmallDevice ? 'flex-start' : 'flex-end'}
                                    sx={{mt: {xs: 1, sm: 0}}}
                                >
                                    {(!element.noToggle && !isDue && !isVIPScore) &&
                                        <>
                                            {(element.autoPopulateCheck && !!safetyChecklist?.[element?.field] && !safetyChecklist?.[element?.field]?.login) &&
                                                <Tooltip title={t('Autofilled by the system')}>
                                                    <ComputerIcon sx={{mr: 1}}/>
                                                </Tooltip>
                                            }

                                            <AgyleBlockSwitch
                                                onChange={e => handleChangeSwitch(e, element)}
                                                value={getValue(element.field)}
                                                disabled={element?.noAction}
                                                nullState
                                                customLabels={{empty: t('Not Required')}}
                                            />
                                        </>
                                    }

                                    {isVIPScore &&
                                        <VisualInfusionPhlebitisSelect
                                            value={getValue(element.field, element.type)}
                                            onChange={e => handleChangeSwitch(e, element)}
                                            openDialog={openVip}
                                            disabled={element?.noAction}
                                        />
                                    }
                                </Grid>

                                <Grid
                                    item
                                    xs={8}
                                    sm={isVIPScore ? 8 : 2}
                                    md={1}
                                    className={classesLocal.divRow}
                                    justifyContent={isSmallDevice ? 'flex-start' : 'flex-end'}
                                    sx={{mt: {xs: 1, sm: 0}}}
                                >
                                    {!isDue &&
                                        <div style={{marginLeft: 5}}>
                                            {!(element?.infoBox || element.noComment) &&
                                                <AgyleButton
                                                    onClick={handleOpenComment.bind(this, element)}
                                                    size="small"
                                                    startIcon={<AddIcon/>}
                                                    colour={(commentButtonMandatory || (isVIPScored && isMandatory)) ? 'error' : 'primary'}
                                                    fullWidth={isSmallDevice}
                                                    width="100%"
                                                >
                                                    {t('Comment')}
                                                </AgyleButton>
                                            }
                                        </div>
                                    }

                                    {isDue &&
                                        <AgyleSelectField
                                            onChange={e => handleChangeDue(e, element)}
                                            options={PSC_DUE_TIME}
                                            value={getValue(element.field, element.type)}
                                            label={t('Hours due')}
                                            withBackground
                                            disabled={element.disabled}
                                            shrink
                                        />
                                    }
                                </Grid>
                            </Grid>

                            {(!_.isEmpty(safetyChecklist[element.field]?.comment) && !element?.textFieldComment) &&
                                <AgyleTextField
                                    value={safetyChecklist[element.field]?.comment}
                                    onChange={() => null}
                                    readOnyl
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            }

                            {element?.textFieldComment &&
                                <AgyleTextField
                                    label={t('Comment')}
                                    value={safetyChecklist[element.field]?.comment || ''}
                                    onChange={e => handleChangeComment(e, element)}
                                    fullWidth
                                    debounce
                                    style={{marginTop: 10}}
                                    noBorder={false}
                                    withNewBackground
                                />
                            }

                            {(element?.infoBar && safetyChecklist[element.field]?.value) &&
                                <InformationBox type="info">
                                    {element?.infoBar}
                                </InformationBox>
                            }

                            {(isDue && element.disabled) &&
                                <InformationBox type="info">
                                    {t('Please complete all fields from this section to unlock next safety check due field.')}
                                </InformationBox>
                            }
                        </div>
                    );
                })}
            </>
        );
    };

    // Section
    const firstHour = useCallback(() => {
        const age = moment.duration(moment(props?.patient?.admission?.startDate).diff(moment(props?.patient?.patient?.dob))).asYears();
        const obs = [
            {
                field: 'triage',
                name: 'Assessment/Triage',
                mandatory: true,
                // noAction: true,
                infoBox: true,
                autoPopulateCheck: true,
            },
            {
                field: 'obsFirst',
                name: 'Vital signs measured + NEWS recorded',
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {
                field: 'obsEscalation',
                name: 'NB: All NEWS 5 or more escalated to Senior Doctor',
                mandatory: true,
                autoPopulateCheck: true,
            },
        ];

        const chestPain = [
            {field: 'ECGRecorded', name: 'ECG recorded (within 10 min)', mandatory: allMandatory},
            {field: 'ECGReviewed', name: 'ECG reviewed by doctor (within 30 min)', mandatory: allMandatory},
        ];

        let other = [
            {
                field: 'undressedAndGown',
                name: 'Undressed and gown',
                mandatory: allMandatory,
                autoPopulateCheck: version === 'UHD',
            },
            {field: 'IDBand', name: 'ID Band', mandatory: allMandatory, autoPopulateCheck: version === 'UHD'},
            {
                field: 'painScore',
                name: 'Pain Score',
                noComment: false,
                mandatoryText: true,
                mandatory: allMandatory,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {
                field: 'analgesia',
                name: 'Analgesia administered (if appropriate)',
                mandatory: allMandatory,
                mandatoryText: true,
                autoPopulateCheck: true,
            },
            {field: 'infectionScreening', name: 'Infection control screening', mandatory: allMandatory},
            {
                field: 'patientAlertsFlags',
                name: 'Check patient alerts/flags - isolation required/escalated',
                mandatory: allMandatory,
            },
            {field: 'diabetic', name: 'Identified as diabetic - BM recorded', mandatory: allMandatory},
            {
                field: 'sepsisSuspected',
                name: 'Sepsis suspected EWS <4 + infection reported',
                noComment: true,
                mandatory: allMandatory,
                autoPopulateCheck: true,
            },
        ];

        if (version === 'UHD') other = _.filter(other, el => el.field !== 'diabetic');

        const other2 = [
            {field: 'sepsisSeniorSignOff', name: 'Sepsis Senior Sign Off', noComment: true, mandatory: allMandatory},
            {
                field: 'IVAccess',
                name: `IV access`,
                mandatoryText: IVCannulaMandatory,
                mandatoryComment: true,
                checkAssessment: true,
                infoBar: IVCannulaMandatory ? 'Comment mandatory: Inserted by, gauge and site of IV cannula' : null,
                mandatory: allMandatory,
            },
        ];
        if (version === 'UHD') {
            other2.push({
                field: 'vipScore',
                name: `Visual Infusion Phlebitis Score`,
                mandatory: allMandatory,
                type: 'vipScore',
            });
        }
        other2.push({field: 'bloodTests', name: 'Blood test', mandatory: allMandatory});

        const specPathway = [
            {field: 'strokeCall', name: 'Consider Stroke Call', mandatory: allMandatory},
            {field: 'specTeamsNotified', name: 'Speciality teams notified', mandatory: allMandatory},
        ];

        const toRender = [
            createRow(obs, 1),
            createRow(chestPain, 1, t('Chest Pain')),
            createRow(other, 1, t('Other')),
            createRow(other2, 1, t('Sepsis Pathway'), true),
        ];

        if (version !== 'UHD') toRender.push(createRow(specPathway, 1, t('Specific Pathway Triggered')));

        if (age > 18) {
            let adults;

            if (version === 'DCH') {
                adults = [
                    {
                        field: 'dementia',
                        name: '4AT Screening for Delirium and Cognitive Impairment',
                        infoBox: true,
                        checkAssessment: true,
                        autoPopulateCheck: true,
                    },
                    {
                        field: 'dementiaScore',
                        name: 'Dementia identified',
                        mandatory: allMandatory,
                        autoPopulateCheck: true,
                    },
                    {
                        field: 'clinicalFrailty',
                        name: 'Clinical Frailty Assessment Completed',
                        infoBox: true,
                        checkAssessment: true,
                        autoPopulateCheck: true,
                    },
                ];
            } else {
                adults = [{
                    field: 'dementiaScore',
                    name: 'Dementia identified flag set',
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                }];
            }
            toRender.push(createRow(adults, 1, t('Adults')));
        }

        if (version === 'UHD') {
            const nursing = [
                {
                    field: 'mentalHealth',
                    name: 'Mental Health Risk Assessment',
                    mandatory: allMandatory,
                    infoBar: 'Please Complete Mental Health Triage MHT17 Form',
                },
                {
                    field: 'nursingComments',
                    name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                    mandatory: allMandatory,
                    noToggle: true,
                    noComment: true,
                    textFieldComment: true,
                },
                {
                    field: 'sectionDue1',
                    name: 'Next Safety Check due',
                    mandatory: true,
                    type: 'due',
                    disabled: !sectionValidation.section1,
                },
            ];

            toRender.push(createRow(nursing, 1));
        }

        return toRender;
    }, [safetyChecklist, sectionValidation, props?.patient?.studies?.safetyChecklist]);

    const secondHour = useCallback(() => {
        const toRender = [];

        const nbm = getPatientFlagValue(props.patient, 'nbm');
        let second = [
            {
                field: 'obsSecond',
                name: 'Vital signs measured + NEWS recorded',
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {
                field: 'painScoreAssessedSecond',
                name: 'Pain score assessed',
                noComment: false,
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {field: 'analgesiaSecond', name: 'Analgesia administered (if appropriate)', mandatoryText: true},
            {
                field: 'nok',
                name: 'NoK identified',
                customBox: 'Information From Patient Record',
                infoBox: true,
                autoPopulateCheck: true,
                mandatory: allMandatory,
            },
            {field: 'nokContacted', name: 'NoK Contacted', mandatory: allMandatory},
            {
                field: 'refreshmentSecond',
                name: 'Refreshments offered (if not NBM)',
                infoBox: nbm,
                autoPopulateCheck: true,
                mandatory: allMandatory,
            },
            {
                field: 'pressureAreas',
                name: `Pressure Areas Examined (${version === 'UHD' ? 'LERN if necessary' : 'Report on Datix as necessary'})`,
                mandatory: allMandatory,
            },
            {field: 'medicinesSecond', name: 'Any medicines charted and administered', mandatory: allMandatory},
            {
                field: 'diabeticPatientsSecond',
                name: 'Diabetic patients: Normal drug regime identified & prescribed',
                mandatory: allMandatory,
            },
            {
                field: 'criticalDrugsGiven',
                name: 'Critical drugs given - insulin, steroids, Parkinson\'s meds',
                mandatory: allMandatory,
            },
        ];

        if (version === 'UHD') {
            second = _.filter(second, el => el.field !== 'refreshmentSecond' && el.field !== 'medicinesSecond' && el.field !== 'diabeticPatientsSecond');
            second.push({
                    field: 'mentalHealthSecond',
                    name: 'Mental Health or challenging behavior observation',
                    mandatory: allMandatory,
                    mandatoryComment: true,
                },
                {
                    field: 'vipScoreSecond',
                    name: `Visual Infusion Phlebitis Score`,
                    mandatory: allMandatory,
                    type: 'vipScore',
                },
                {
                    field: 'nursingCommentsSecond',
                    name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                    mandatory: allMandatory,
                    noToggle: true,
                    noComment: true,
                    textFieldComment: true,
                },
            );
        }

        toRender.push(createRow(second, 2));

        if (version === 'UHD') {
            const other2 = [
                {
                    field: 'IVAccessSecond',
                    name: `New IV access since last check?`,
                    mandatoryText: IVCannulaMandatory,
                    mandatoryComment: true,
                    checkAssessment: true,
                    infoBar: IVCannulaMandatory ? 'Comment mandatory: Inserted by, gauge and site of IV cannula' : null,
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'bloodTestsSecond',
                    name: 'Blood tests since last check?',
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
            ];

            toRender.push(createRow(other2, 2));
            toRender.push(createRow([{
                field: 'sectionDue2',
                name: 'Next Safety Check due',
                mandatory: true,
                type: 'due',
                disabled: !sectionValidation.section2,
            }], 2));
        }
        return toRender;
    }, [safetyChecklist, sectionValidation, props?.patient]);

    const thirdHour = useCallback(() => {
        const toRender = [];

        const nbm = getPatientFlagValue(props.patient, 'nbm');
        let third = [
            {
                field: 'obsThird',
                name: 'Vital signs measured + NEWS recorded',
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {
                field: 'painScoreAssessedThird',
                name: 'Pain score assessed',
                noComment: false,
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {field: 'analgesiaThird', name: 'Analgesia administered (if appropriate)', mandatoryText: true},
            {field: 'medicinesThird', name: 'Any medicines charted and administered', mandatory: allMandatory},
            {
                field: 'refreshmentThird',
                name: 'Refreshments offered (if not NBM)',
                infoBox: nbm,
                autoPopulateCheck: true,
                mandatory: allMandatory,
            },
            {
                field: 'patientUpdatePlan',
                name: `Patient updated with plan${version === 'UHD' ? ' and welfare checked?' : ''}`,
                mandatory: allMandatory,
            },
            {field: 'diabeticPatientsThird', name: 'Diabetic patients: condition reassessed', mandatory: allMandatory},
        ];

        if (version === 'UHD') {
            third = _.filter(third, el => el.field !== 'diabeticPatientsThird');
            third.push({
                    field: 'mentalHealthThird',
                    name: 'Mental Health or challenging behavior observation',
                    mandatory: allMandatory,
                    mandatoryComment: true,
                },
                {
                    field: 'nursingCommentsThird',
                    name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                    mandatory: allMandatory,
                    noToggle: true,
                    noComment: true,
                    textFieldComment: true,
                });
        }

        toRender.push(createRow(third, 3));

        if (version === 'UHD') {
            const other2 = [
                {
                    field: 'IVAccessThird',
                    name: `New IV access since last check?`,
                    mandatoryText: IVCannulaMandatory,
                    mandatoryComment: true,
                    checkAssessment: true,
                    infoBar: IVCannulaMandatory ? 'Comment mandatory: Inserted by, gauge and site of IV cannula' : null,
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'vipScoreThird',
                    name: `Visual Infusion Phlebitis Score`,
                    mandatory: allMandatory,
                    type: 'vipScore',
                },
                {
                    field: 'bloodTestsThird',
                    name: 'Blood tests since last check?',
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'sectionDue3',
                    name: 'Next Safety Check due',
                    mandatory: true,
                    type: 'due',
                    disabled: !sectionValidation.section3,
                },
            ];

            toRender.push(createRow(other2, 3));
        }

        return toRender;
    }, [safetyChecklist, sectionValidation, props?.patient]);

    const fourthHour = useCallback(() => {
        const toRender = [];

        const nbm = getPatientFlagValue(props.patient, 'nbm');
        let fourth = [
            {
                field: 'obsFourth',
                name: 'Vital signs measured + NEWS recorded',
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {
                field: 'painScoreAssessedFourth',
                name: 'Pain score assessed',
                noComment: false,
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
                autoPopulateCheck: true,
            },
            {field: 'analgesiaFourth', name: 'Analgesia administered (if appropriate)', mandatoryText: true},
            {
                field: 'refreshmentFourth',
                name: 'Refreshments offered (if not NBM)',
                infoBox: nbm,
                autoPopulateCheck: true,
                mandatory: allMandatory,
            },
            {field: 'medicinesFourth', name: 'Any medicines charted and administered', mandatory: allMandatory},
            {
                field: 'diabeticPatientsFourth',
                name: 'Diabetic patients: condition reassessed',
                mandatory: allMandatory,
            },
        ];

        if (version === 'UHD') {
            fourth = _.filter(fourth, el => el.field !== 'diabeticPatientsFourth');
            fourth.push(
                {
                    field: 'patientUpdatePlanFourth',
                    name: `Patient updated with plan and welfare checked?`,
                    mandatory: allMandatory,
                },
                {
                    field: 'mentalHealthFourth',
                    name: 'Mental Health or challenging behavior observation',
                    mandatory: allMandatory,
                    mandatoryComment: true,
                },
                {
                    field: 'nursingCommentsFourth',
                    name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                    mandatory: allMandatory,
                    noToggle: true,
                    noComment: true,
                    textFieldComment: true,
                });
        }
        toRender.push(createRow(fourth, 4));

        if (version === 'UHD') {
            const other2 = [
                {
                    field: 'IVAccessFourth',
                    name: `New IV access since last check?`,
                    mandatoryText: IVCannulaMandatory,
                    mandatoryComment: true,
                    checkAssessment: true,
                    infoBar: IVCannulaMandatory ? 'Comment mandatory: Inserted by, gauge and site of IV cannula' : null,
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'vipScoreFourth',
                    name: `Visual Infusion Phlebitis Score`,
                    mandatory: allMandatory,
                    type: 'vipScore',
                },
                {
                    field: 'bloodTestsFourth',
                    name: 'Blood tests since last check?',
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'sectionDue4',
                    name: 'Next Safety Check due',
                    mandatory: true,
                    type: 'due',
                    disabled: !sectionValidation.section4,
                },
            ];

            toRender.push(createRow(other2, 4));
        }

        return toRender;
    }, [safetyChecklist, sectionValidation, props?.patient]);

    const admission = () => {
        const admission = [
            {field: 'wardTransfer', name: 'Ready for ward transfer'},
            {field: 'bed', name: 'Bed confirmed'},
            {field: 'propertyFormComplete', name: 'Property form complete'},
        ];

        return (
            <>
                {createRow(admission, '')}
            </>
        );
    };

    const strokeList = () => {
        const rows = [
            {title: 'Has there been LOC or syncope?', value: 'LOCOrSyncope', type: 'minus'},
            {title: 'Has there been seizure activity?', value: 'seizure', type: 'minus', noBorder: true},
            {
                title: 'Has there been NEW ACUTE onset (including wakening from sleep) of:',
                value: '',
                type: null,
                noBorder: true,
            },
            {title: 'Asymmetric facial weakness', value: 'asymmetricFacial', type: 'plus'},
            {title: 'Asymmetric arm weakness', value: 'asymmetricArm', type: 'plus'},
            {title: 'Asymmetric leg weakness', value: 'asymmetricLeg', type: 'plus'},
            {title: 'Speech disturbance', value: 'speech', type: 'plus'},
            {title: 'Visual field deficit', value: 'visual', type: 'plus', noBorder: true},
            {title: '', value: '', type: 'score', noBorder: true},
        ];

        const renderRows = ({title, value, type, noBorder}) => {
            return (
                <TableRow key={value + type}>
                    <TableCell
                        size="small"
                        sx={{fontWeight: value !== '' ? 'normal' : 'bold'}}
                        className={clsx(noBorder && classesLocal.noBottomBorder)}
                    >
                        {t(title)}
                    </TableCell>

                    <TableCell
                        size="small"
                        align="right"
                        className={clsx(noBorder && classesLocal.noBottomBorder)}
                    >
                        {value !== '' &&
                            <ToggleButtonGroup
                                value={safetyChecklist.stroke[value]}
                                exclusive
                                onChange={handleChange}
                            >
                                <ToggleButton
                                    name={value}
                                    value={type === 'minus' ? '-1' : '+1'}
                                    className={classesLocal.toggle}
                                >
                                    {type === 'minus' ? '-1' : '+1'}
                                </ToggleButton>

                                <ToggleButton
                                    name={value}
                                    value="0"
                                    className={classesLocal.toggle}
                                >
                                    0
                                </ToggleButton>
                            </ToggleButtonGroup>
                        }
                        {type === 'score' &&
                            <div className={classesLocal.score}>
                                <span style={{float: 'left'}}>
                                    {t('Score: ')}
                                </span>

                                <span>
                                    {strokeScore()}
                                </span>
                            </div>
                        }
                    </TableCell>
                </TableRow>
            );
        };

        const handleChange = (event) => {
            const {value, name} = event.target;
            setSafetyChecklist(prevState => ({
                ...prevState,
                stroke: {
                    ...prevState.stroke,
                    by: tester,
                    date: moment().toISOString(),
                    [name]: value,
                },
            }));
        };

        return (
            <>
                <div className={classesLocal.row}>
                    <InformationBox type="info">
                        {t('Score of 0 or less - Stroke diagnosis is unlikely, although this diagnosis is not entirely excluded.')}
                    </InformationBox>
                </div>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classesLocal.noBottomBorder}>
                                {t('Description')}
                            </TableCell>

                            <TableCell align="right" className={classesLocal.noBottomBorder}>
                                <span>{t('Yes')}</span>
                                <span style={{marginLeft: 40, marginRight: 20}}>{t('No')}</span>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map(element => (
                            renderRows(element)
                        ))}
                    </TableBody>
                </Table>
            </>
        );
    };

    // Create row for a dynamic section of a check list
    const creatRowForNewSection = (section) => {
        const nbm = getPatientFlagValue(props.patient, 'nbm');
        let newSection = [
            {
                field: 'obs',
                name: 'Vital signs measured + NEWS recorded',
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
            },
            {
                field: 'painScore',
                name: 'Pain score assessed',
                noComment: true,
                mandatoryText: true,
                infoBox: true,
                checkAssessment: true,
            },
            {field: 'analgesia', name: 'Analgesia administered (if appropriate)', mandatoryText: true},
            {
                field: 'refreshment',
                name: 'Refreshments offered (if not NBM)',
                infoBox: nbm,
                mandatory: allMandatory,
            },
            {
                field: 'medicines',
                name: version === 'UHD' ? 'Any medicines charted and administered (Consider EPMA)' : 'Regular medications administered (if appropriate)',
                mandatory: allMandatory,
            },
            {field: 'diabeticPatients', name: 'Diabetic patients: condition reassessed', mandatory: allMandatory},
        ];

        if (version === 'UHD') {
            if (section?.counter === 6) {
                newSection = [
                    {
                        field: 'reasonForDelay',
                        name: 'Reason for Delay',
                        mandatory: allMandatory,
                        noToggle: true,
                        noComment: true,
                        textFieldComment: true,
                    },
                    {
                        field: 'delayExplained',
                        name: 'Delay explained to patient?',
                        mandatory: allMandatory,
                    },
                    {
                        field: 'transferredIntoHospitalBed',
                        name: 'Has the patient been transferred into a hospital bed?',
                        mandatory: allMandatory,
                    },
                    {
                        field: 'nurseAware',
                        name: 'Nurse in charge aware?',
                        mandatory: allMandatory,
                    },
                    {
                        field: 'medicines',
                        name: 'Any medicines charted and administered (Consider EPMA)',
                        mandatory: allMandatory,
                    },
                    {
                        field: 'mentalHealth',
                        name: 'Mental Health or challenging behavior observation',
                        mandatory: allMandatory,
                        mandatoryComment: true,
                    },
                    {
                        field: 'nursingComments',
                        name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                        mandatory: allMandatory,
                        noToggle: true,
                        noComment: true,
                        textFieldComment: true,
                    },
                ];
            } else {
                newSection = _.filter(newSection, el => el.field !== 'diabeticPatients');
                newSection.push(
                    {
                        field: 'patientUpdatePlan',
                        name: `Patient updated with plan and welfare checked?`,
                        mandatory: allMandatory,
                    },
                    {
                        field: 'mentalHealth',
                        name: 'Mental Health or challenging behavior observation',
                        mandatory: allMandatory,
                        mandatoryComment: true,
                    },
                    {
                        field: 'nursingComments',
                        name: 'Nursing Comments (Include details of all care delivery or escalations/issues)',
                        mandatory: allMandatory,
                        noToggle: true,
                        noComment: true,
                        textFieldComment: true,
                    },
                );
            }
            newSection.push(
                {
                    field: 'IVAccess',
                    name: `New IV access since last check?`,
                    mandatoryText: IVCannulaMandatory,
                    mandatoryComment: true,
                    checkAssessment: true,
                    infoBar: IVCannulaMandatory ? 'Comment mandatory: Inserted by, gauge and site of IV cannula' : null,
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'vipScore',
                    name: `Visual Infusion Phlebitis Score`,
                    mandatory: allMandatory,
                    type: 'vipScore',
                },
                {
                    field: 'bloodTests',
                    name: 'Blood tests since last check?',
                    mandatory: allMandatory,
                    autoPopulateCheck: true,
                },
                {
                    field: 'sectionDue',
                    name: 'Next Safety Check due',
                    mandatory: true,
                    type: 'due',
                },
            );
        }

        const handleChangeSwitch = (value, element) => {
            const isNR = _.isNull(value);
            handleChangeList(value, element.field, isNR, '', element, section, element.autoPopulateCheck, element.defaultComment);
        };

        const handleChangeDue = (value, element) => {
            handleChangeList(value, element.field, false, '', element, section, element.autoPopulateCheck, element.defaultComment);
        };

        const getValue = (element, type) => {
            const val = section[element];
            if (val?.notRequired) return null;
            else if (type === 'due') return val?.value || '';
            else if (type === 'vipScore') return val?.value;
            else if (_.isBoolean(val?.value)) return val.value;

            return -1;
        };

        const handleOpenComment = (element) => {
            field.current = element.field;
            sections.current = section;

            setComment(section[element.field]?.comment || '');
            openComment();
        };

        const handleChangeComment = (e, element) => {
            const {value} = e.target;

            const clone = _.cloneDeep(safetyChecklist.nextHours);
            let sectionClone = _.cloneDeep(section);

            sectionClone = {
                ...sectionClone,
                [element.field]: {
                    ...sectionClone[element.field],
                    comment: value,
                    by: tester,
                    login: userObject?.userName,
                    date: moment().toISOString(),
                },
            };

            addOrReplaceSection(clone, sectionClone);
            setSafetyChecklist(prevState => ({
                ...prevState,
                nextHours: clone,
            }));
        };

        const checkValue = (val) => {
            return (_.isNull(val?.value) && !_.isBoolean(val?.value)) || (_.isNull(val?.notRequired) && !_.isBoolean(val?.notRequired)) || _.isNil(val);
        };

        return (
            newSection.map((element, index) => {
                const isDue = element.type === 'due';
                const isVIPScore = element.type === 'vipScore';
                let isVIPScored = false;

                let isMandatory = (element.mandatory || element.mandatoryText)
                    && checkValue(section[element.field]);

                if (element.textFieldComment) {
                    isMandatory = isMandatory && _.isEmpty(section[element.field]?.comment);
                }

                if (element.mandatoryComment && section[element.field]?.value) {
                    isMandatory = _.isEmpty(section[element.field]?.comment);
                }

                if (isVIPScore) {
                    const val = Number(getValue(element.field, element.type));
                    if (_.isNumber(val) && !_.isNaN(val)) {
                        isMandatory = val > 1 ? _.isEmpty(section[element.field]?.comment) : false;
                        isVIPScored = true;
                    } else {
                        isMandatory = true;
                    }
                }

                const commentButtonMandatory = element.mandatoryComment && _.isEmpty(section[element.field]?.comment) && section[element.field]?.value === true;

                return (
                    <div key={element.field}>
                        {index > 0 && <Divider style={{margin: '6px 0'}}/>}

                        <Grid
                            container direction="row"
                            className={clsx(classesLocal.row)}
                            columns={8}
                        >
                            <Grid item xs={8} sm={3} md={isVIPScore ? 4 : 5}>
                                <div
                                    className={clsx(classesLocal.text, isMandatory && classesLocal.mandatory)}
                                >
                                    {t(element.name)}{isMandatory ? '*' : ''}
                                </div>

                                <div className={classesLocal.creatorText}>
                                    {`${section[element.field]?.by || ''} ${section[element.field]?.date
                                        ? moment(section[element.field].date).format(defaultDateTimeFormat)
                                        : ''
                                    }`}
                                </div>
                            </Grid>

                            <Grid item xs={8} sm={isVIPScore ? 8 : 3} md={isVIPScore ? 3 : 2}
                                  className={classesLocal.divRow}
                                  justifyContent="flex-end"
                                  sx={{mt: {xs: 1, sm: 0}}}
                            >
                                {(!element.noToggle && !isDue && !isVIPScore) &&
                                    <>
                                        {(element.autoPopulateCheck && !!section?.[element?.field] && !section?.[element?.field]?.login) &&
                                            <Tooltip title={t('Autofilled by the system')}>
                                                <ComputerIcon sx={{mr: 1}}/>
                                            </Tooltip>
                                        }

                                        <AgyleBlockSwitch
                                            onChange={e => handleChangeSwitch(e, element)}
                                            value={getValue(element.field)}
                                            disabled={element?.noAction}
                                            nullState
                                            customLabels={{empty: t('Not Required')}}
                                        />
                                    </>
                                }

                                {isVIPScore &&
                                    <VisualInfusionPhlebitisSelect
                                        value={getValue(element.field, element.type)}
                                        onChange={e => handleChangeSwitch(e, element)}
                                        openDialog={openVip}
                                        disabled={element?.noAction}
                                    />
                                }
                            </Grid>

                            <Grid item xs={8} sm={isVIPScore ? 8 : 2} md={1} className={classesLocal.divRow}
                                  justifyContent="flex-end"
                                  sx={{mt: {xs: 1, sm: 0}}}
                            >
                                {!isDue &&
                                    <div style={{marginLeft: 5}}>
                                        {!(element?.infoBox || element.noComment) &&
                                            <AgyleButton
                                                onClick={handleOpenComment.bind(this, element)}
                                                size="small"
                                                startIcon={<AddIcon/>}
                                                colour={(commentButtonMandatory || (isVIPScored && isMandatory)) ? 'error' : 'primary'}
                                            >
                                                {t('Comment')}
                                            </AgyleButton>
                                        }
                                    </div>
                                }

                                {isDue &&
                                    <AgyleSelectField
                                        onChange={e => handleChangeDue(e, element)}
                                        options={PSC_DUE_TIME}
                                        value={getValue(element.field, element.type)}
                                        label={t('Hours due')}
                                        withBackground
                                        disabled={element.disabled}
                                        shrink
                                    />
                                }
                            </Grid>
                        </Grid>

                        {(!_.isEmpty(section[element.field]?.comment) && !element?.textFieldComment) &&
                            <AgyleTextField
                                value={section[element.field]?.comment}
                                onChange={() => null}
                                fullWidth
                                readOnly
                                noBorder={false}
                                withNewBackground
                            />
                        }

                        {element?.textFieldComment &&
                            <AgyleTextField
                                label={t('Comment')}
                                value={section[element.field]?.comment}
                                onChange={e => handleChangeComment(e, element)}
                                fullWidth
                                debounce
                                style={{marginTop: 10}}
                                noBorder={false}
                                withNewBackground
                            />
                        }

                        {(element?.infoBar && section[element.field]?.value) &&
                            <InformationBox type="info">
                                {element?.infoBar}
                            </InformationBox>
                        }
                    </div>
                );
            })
        );
    };

    // Render dynamic section
    const renderNewSections = () => {
        return (
            safetyChecklist.nextHours?.length > 0 && (
                safetyChecklist.nextHours.map(element => {
                    return (
                        <AgyleAccordion
                            key={element._id}
                            expanded={expanded === element._id}
                            onChange={handleChangeAccordion(element._id)}
                            label={t(version === 'UHD' ? `${element.counter}th Safety & Welfare Check` : `${element.counter}th Hour Completion Time`)}
                        >
                            {creatRowForNewSection(element)}
                        </AgyleAccordion>
                    );
                })
            )
        );
    };

    return (
        <>
            <Grid item xs={12} style={{marginBottom: 10}}>
                <ObservationTable patient={props.patient}/>
            </Grid>

            <InformationBox type="info">
                {t('NOTE: To proceed to next hour all mandatory tasks must be marked as yes or not required')}
            </InformationBox>

            <AgyleAccordion
                expanded={expanded === 'first'}
                onChange={handleChangeAccordion('first')}
                label={t(version === 'UHD' ? 'Admission and Initial Safety Check' : '1st Hour Completion Time')}
                details={firstHour()}
                formBackground
            />

            <AgyleAccordion
                expanded={expanded === 'second'}
                disabled={!sectionValidation.section1 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue1?.value) : false)}
                onChange={handleChangeAccordion('second')}
                label={t(version === 'UHD' ? '2nd Safety & Welfare Check' : '2nd Hour Completion Time')}
                secondLabel={(!sectionValidation.section1 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue1?.value) : false)) ? t('Complete all mandatory fields from previous section') : ''}
                secondHeadingStyle={classesLocal.accordionMandatoryText}
                details={secondHour()}
                formBackground
            />

            <AgyleAccordion
                expanded={expanded === 'third'}
                disabled={!sectionValidation.section2 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue2?.value) : false)}
                onChange={handleChangeAccordion('third')}
                label={t(version === 'UHD' ? '3rd Safety & Welfare Check' : '3rd Hour Completion Time')}
                secondLabel={(!sectionValidation.section2 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue2?.value) : false)) ? t('Complete all mandatory fields from previous section') : ''}
                secondHeadingStyle={classesLocal.accordionMandatoryText}
                details={thirdHour()}
                formBackground
            />

            <AgyleAccordion
                expanded={expanded === 'fourth'}
                disabled={!sectionValidation.section3 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue3?.value) : false)}
                onChange={handleChangeAccordion('fourth')}
                label={t(version === 'UHD' ? '4th Safety & Welfare Check' : '4th Hour Completion Time')}
                secondLabel={(!sectionValidation.section3 || (version === 'UHD' ? _.isNil(safetyChecklist?.sectionDue3?.value) : false)) ? t('Complete all mandatory fields from previous section') : ''}
                secondHeadingStyle={classesLocal.accordionMandatoryText}
                details={fourthHour()}
                formBackground
            />

            {!_.isEmpty(safetyChecklist?.nextHours) && (
                renderNewSections()
            )}

            {!disableReadyToAdmission &&
                <AgyleAccordion
                    expanded={expanded === 'admission'}
                    onChange={handleChangeAccordion('admission')}
                    label={t('Patient Ready for Admission')}
                    formBackground
                >
                    {admission()}
                </AgyleAccordion>
            }

            {!disableRosierAssessment &&
                <AgyleAccordion
                    expanded={expanded === 'stroke'}
                    onChange={handleChangeAccordion('stroke')}
                    label={t('ROSIER Assessment')}
                    formBackground
                >
                    {strokeList()}
                </AgyleAccordion>
            }

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={addNewSection}
                onClose={closeConfirmNewSection}
                message={t('Are you sure you want to add new section to safety checklist?')}
                open={isConfirmNewSectionOpen}
            />

            <MessageBox
                open={isCommentOpen}
                title={t('Add Comment')}
                haveAccept={{yes: t('Submit')}}
                onAccept={handleAddComment}
                onClose={handleCloseComment}
                messageObject={renderComment()}
                fullWidth
                maxWidth="lg"
                disableSave
                disabled={_.isEmpty(comment)}
            />

            <VisualInfusionPhlebitisModal
                open={isVipOpen}
                onClose={closeVip}
            />
        </>
    );
};

// PatientSafetyChecklist.whyDidYouRender = true;

function areEqual(prevProps, nextProps) {
    let isEqual = _.isEqual(prevProps.patient, nextProps.patient);
    isEqual = isEqual && _.isEqual(prevProps.disableSave, nextProps.disableSave);
    isEqual = isEqual && _.isEqual(prevProps.open, nextProps.open);
    isEqual = isEqual && _.isEqual(prevProps.pastAttendances, nextProps.pastAttendances);
    isEqual = isEqual && _.isEqual(prevProps.patientAgeType, nextProps.patientAgeType);
    isEqual = isEqual && _.isEqual(prevProps.patientType, nextProps.patientType);
    isEqual = isEqual && _.isEqual(prevProps._containerWidth, nextProps._containerWidth);

    return isEqual;
}

export default React.memo(PatientSafetyChecklist, areEqual);